/* eslint-disable @typescript-eslint/ban-ts-comment */
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { RootState } from '../store/store';

import {
  AreaTargetCatalogResponse,
  AreaTargetCatalogSearchRequest,
  CreateUserNoteResponse,
  CreateUserNotificationRuleResponse,
  DeleteMessageNotificationsTelegramSubscriptionResponse,
  DeleteUserNoteResponse,
  DeleteUserNotificationRuleResponse,
  FleetInfo,
  GetAnchoragesOnMapResponse,
  GetAreaCardResponse,
  GetAreaCatalogResponse,
  GetMessageNotificationsTelegramSubscriptionResponse,
  GetMessageNotificationsTelegramSubscriptionTokenResponse,
  GetNotificationRulesResponse,
  GetPortCardResponse,
  GetTerminalCardResponse,
  GetTerminalsOnMapResponse,
  GetUserApiKeyResponse,
  GetUserNotesForObjectDefinitionResponse,
  GetUserNotificationsResponse,
  GetUserProfileSettingsResponse,
  GetUserSystemConfigurationResponse,
  GetUserSystemNotificationResponse,
  LastSafetyEventsResponse,
  MapPlaybackResponse,
  MapSnapshotRequest,
  MapSnapshotResponse,
  MapViewportRequest,
  MapViewportResponse,
  PatchUserProfileSettingsResponse,
  PlaybackSearchParams,
  PointGeoInfoRequest,
  PointGeoInfoResponse,
  PortTargetArrivalsFilterResponse,
  PortTargetArrivalsSearchResponse,
  PortTargetCatalogCsvReportRequest,
  PortTargetCatalogFilterResponse,
  PortTargetCatalogRequest,
  PortTargetEventsRequest,
  PortTargetEventsResponse,
  PortTerminalsRequest,
  PortTerminalsResponse,
  RecreateUserNoteResponse,
  SearchUserNotesForObjectDefinitionResponse,
  SimpleRouteRequest,
  SimpleRouteResponse,
  TargetAnchorageEventsRequest,
  TargetAnchorageEventsResponse,
  TargetArrivalsCsvReportRequest,
  TargetArrivalsSearchRequest,
  TargetCatalogResponse,
  TargetCatalogSearchRequest,
  TargetPortEventsRequest,
  TargetPortEventsResponse,
  TerminalsOnMapRequest,
  TerminalTargetCatalogRequest,
  TerminalTargetCatalogResponse,
  UserProfileSettingsPatchParams,
  ViewportFilter,
  WithinDistanceTargetCatalogRequest,
  WithinDistanceTargetCatalogResponse,
  WorldFleetResponse,
} from 'ais-facade-api/api';
import i18next from 'i18next';
import { onQueryStartedErrorToast } from './apiErrorHandler';

type FileT = {
  url: string;
  filename: string;
};

const BASE_URL = process.env?.NODE_ENV === 'development' ? window._env_.APP_DEV_API : window._env_.APP_API;

export const facadeApi = createApi({
  reducerPath: 'facade',
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL + '/facade/versions/1',

    credentials: 'include',
    mode: 'cors',
    prepareHeaders: (headers: Headers, { getState }) => {
      const userInfo = (getState() as RootState).userSlice;
      headers.set('Accept-Language', i18next.resolvedLanguage ? i18next.resolvedLanguage : 'ru');

      if (userInfo.auth && userInfo.user) {
        headers.set('x-ais-user-csrf', userInfo.auth.value.csrfToken);
        headers.set('ais-user-csrf', userInfo.auth.value.csrfToken);
      }
      return headers;
    },
  }),
  tagTypes: ['Facade', 'Profile', 'Notifications-rules', 'Ship-note'],
  endpoints: (builder) => ({
    getHistory: builder.query<FileT, number>({
      query: (mmsi: number) => {
        return {
          url: `/report/xlsx/vessel-history/${mmsi}`,
          withCredentials: true,
          headers: { accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },

          responseHandler: async (res) => {
            const blobFile = await res.blob();

            const filename = `${res.headers.get('Content-Disposition')?.split('filename=')[1]}` || 'history.xlsx';
            const url = window.URL.createObjectURL(new Blob([blobFile]));
            return {
              url,
              filename,
            };
          },
        };
      },
      onQueryStarted: onQueryStartedErrorToast,
    }),

    getArrivalsCsv: builder.mutation<any, { body: TargetArrivalsCsvReportRequest; key: string }>({
      query: ({ key, body }) => {
        return {
          url: `/port/${key}/target-arrivals/export/csv`,
          method: 'POST',
          responseType: 'blob',
          headers: { mediaType: 'text/csv' },
          body,
          responseHandler: async (res) => {
            const blobFile = await res.blob();
            const filename = `${res.headers.get('Content-Disposition')?.split('filename=')[1]}` || 'arrivals.xlsx';
            const url = window.URL.createObjectURL(new Blob([blobFile]));
            return {
              url,
              filename,
            };
          },
        };
      },
      onQueryStarted: onQueryStartedErrorToast,
    }),

    getFilterOptions: builder.query<{ filter: ViewportFilter }, void>({
      query: () => {
        return {
          url: '/filter/map/viewport',
          headers: { accept: 'application/json' },
          overrideExisting: false,
          withCredentials: true,
          responseHandler: async (res) => {
            return await res.json();
          },
        };
      },
      onQueryStarted: onQueryStartedErrorToast,
    }),

    getTargets: builder.mutation<MapViewportResponse, MapViewportRequest>({
      query: (data) => {
        return {
          url: '/map',
          headers: { accept: 'application/json', 'Content-type': 'application/json' },
          method: 'POST',
          body: data,
          cache: 'no-cache',
          overrideExisting: false,
          withCredentials: true,
        };
      },
      onQueryStarted: onQueryStartedErrorToast,
      invalidatesTags: [{ type: 'Facade' }],
    }),

    getUserFleet: builder.query<FleetInfo[], object>({
      query: (sorting) => ({
        url: '/fleet',
        params: sorting,
        cache: 'no-cache',
        withCredentials: true,
        overrideExisting: false,
      }),
      onQueryStarted: onQueryStartedErrorToast,
      transformResponse: (response: { results: FleetInfo[] }) => response?.results,
      providesTags: [{ type: 'Facade' }],
    }),

    getVesselCatalog: builder.mutation<TargetCatalogResponse, TargetCatalogSearchRequest>({
      query: (body) => {
        return {
          url: '/target-catalog',
          headers: { accept: 'application/json', 'Content-type': 'application/json' },
          method: 'POST',
          body,
          cache: 'no-cache',
          withCredentials: true,
          overrideExisting: false,
        };
      },
      onQueryStarted: onQueryStartedErrorToast,
    }),

    getPortArrivalsData: builder.mutation<PortTargetArrivalsSearchResponse, { key: string; body: TargetArrivalsSearchRequest }>({
      query: ({ key, body }) => {
        return {
          url: `/port/${key}/target-arrivals`,
          headers: { accept: 'application/json', 'Content-type': 'application/json' },
          method: 'POST',
          body,
          cache: 'no-cache',
          withCredentials: true,
          overrideExisting: false,
        };
      },
      onQueryStarted: onQueryStartedErrorToast,
    }),
    getPortArrivalsFiltersOptions: builder.query<PortTargetArrivalsFilterResponse, void>({
      query: () => {
        return {
          url: '/filter/port/target-arrivals',
          headers: { accept: 'application/json' },
          overrideExisting: false,
          withCredentials: true,
          responseHandler: async (res) => {
            return await res.json();
          },
        };
      },
      onQueryStarted: onQueryStartedErrorToast,
    }),
    getMapSnapshot: builder.mutation<MapSnapshotResponse, MapSnapshotRequest>({
      query: (body) => ({
        url: '/snapshot',
        method: 'POST',
        body,
      }),
      onQueryStarted: onQueryStartedErrorToast,
    }),
    getAreaCard: builder.query<GetAreaCardResponse, string | undefined>({
      query: (code) => {
        return {
          url: `/area/${code}/card`,
          skipToken: !code,
        };
      },
      onQueryStarted: onQueryStartedErrorToast,
    }),
    getVesselCatalogInArea: builder.mutation<
      AreaTargetCatalogResponse,
      { body: AreaTargetCatalogSearchRequest; requestParam: string }
    >({
      query: ({ body, requestParam }) => {
        return {
          url: `/area/${requestParam}/target-catalog`,
          headers: { accept: 'application/json', 'Content-type': 'application/json' },
          method: 'POST',
          body,
        };
      },
      onQueryStarted: onQueryStartedErrorToast,
    }),
    getPortTargetCatalog: builder.mutation<TargetCatalogResponse, { body: PortTargetCatalogRequest; requestParam: string }>({
      query: ({ body, requestParam }) => {
        return {
          url: `/port/${requestParam}/target-catalog`,
          headers: { accept: 'application/json', 'Content-type': 'application/json' },
          method: 'POST',
          body,
        };
      },
      onQueryStarted: onQueryStartedErrorToast,
    }),
    getPortTargetCatalogFilterOptions: builder.query<PortTargetCatalogFilterResponse, void>({
      query: () => {
        return {
          url: '/filter/port/target-catalog',
          headers: { accept: 'application/json' },
          overrideExisting: false,
          withCredentials: true,
          responseHandler: async (res) => {
            return await res.json();
          },
        };
      },
      onQueryStarted: onQueryStartedErrorToast,
    }),
    getPortCard: builder.query<GetPortCardResponse, string>({
      query: (key) => {
        return {
          url: `/port/${key}/card`,
          skipToken: !key,
        };
      },
      onQueryStarted: onQueryStartedErrorToast,
    }),
    getVesselsInPortCsv: builder.mutation<any, { body: PortTargetCatalogCsvReportRequest; key: string }>({
      query: ({ key, body }) => {
        return {
          url: `/port/${key}/target-catalog/export/csv`,
          method: 'POST',
          responseType: 'blob',
          headers: { mediaType: 'text/csv' },
          body,
          responseHandler: async (res) => {
            const blobFile = await res.blob();
            const filename = `${res.headers.get('Content-Disposition')?.split('filename=')[1]}` || 'Vessels_In_The_Port.xlsx';
            const url = window.URL.createObjectURL(new Blob([blobFile]));
            return {
              url,
              filename,
            };
          },
        };
      },
      onQueryStarted: onQueryStartedErrorToast,
    }),

    getTerminalsOnMap: builder.mutation<GetTerminalsOnMapResponse, TerminalsOnMapRequest>({
      query: (body) => {
        return {
          url: `/terminal/map`,
          method: 'POST',
          body,
        };
      },
      onQueryStarted: onQueryStartedErrorToast,
    }),
    getTerminalCard: builder.query<GetTerminalCardResponse, string | undefined>({
      query: (key) => {
        return {
          url: `/terminal/${key}/card`,
          skipToken: !key,
        };
      },
      onQueryStarted: onQueryStartedErrorToast,
    }),
    getTerminalTargetCatalog: builder.mutation<
      TerminalTargetCatalogResponse,
      { body: TerminalTargetCatalogRequest; requestParam: string }
    >({
      query: ({ body, requestParam }) => {
        return {
          url: `/terminal/${requestParam}/target-catalog`,
          headers: { accept: 'application/json', 'Content-type': 'application/json' },
          method: 'POST',
          body,
        };
      },
      onQueryStarted: onQueryStartedErrorToast,
    }),
    getTerminalCatalog: builder.mutation<PortTerminalsResponse, { body: PortTerminalsRequest; key: string }>({
      query: ({ body, key }) => {
        return {
          url: `/port/${key}/terminals`,
          headers: { accept: 'application/json', 'Content-type': 'application/json' },
          method: 'POST',
          body,
        };
      },
      onQueryStarted: onQueryStartedErrorToast,
    }),
    getSimpleRoute: builder.mutation<SimpleRouteResponse, SimpleRouteRequest>({
      query: (body) => {
        return {
          url: '/route/simple',
          method: 'POST',
          body,
        };
      },
    }),
    getAreasCatalog: builder.query<GetAreaCatalogResponse, { type?: string; subType?: string; fetchGeometry?: boolean }>({
      query: ({ type, subType, fetchGeometry = false }) => ({
        url: `/area-catalog`,
        params: { type, subType, fetchGeometry },
      }),
      onQueryStarted: onQueryStartedErrorToast,
    }),
    getPortEventsData: builder.mutation<PortTargetEventsResponse, { body: PortTargetEventsRequest; key: string }>({
      query: ({ key, body }) => {
        return {
          url: `/port/${key}/target-events`,
          headers: { accept: 'application/json', 'Content-type': 'application/json' },
          method: 'POST',
          body,
          cache: 'no-cache',
          withCredentials: true,
          overrideExisting: false,
        };
      },
      onQueryStarted: onQueryStartedErrorToast,
    }),
    getVesselEventsData: builder.mutation<TargetPortEventsResponse, { body: TargetPortEventsRequest; mmsi: string }>({
      query: ({ mmsi, body }) => {
        return {
          url: `/targets/${mmsi}/port-events`,
          headers: { accept: 'application/json', 'Content-type': 'application/json' },
          method: 'POST',
          body,
          cache: 'no-cache',
          withCredentials: true,
          overrideExisting: false,
        };
      },
      onQueryStarted: onQueryStartedErrorToast,
    }),
    getUserProfile: builder.query<GetUserProfileSettingsResponse, void>({
      query: () => ({
        url: `/user/profile-settings`,
      }),
      providesTags: [{ type: 'Profile' }],
      onQueryStarted: onQueryStartedErrorToast,
    }),
    updateUserProfileSettings: builder.mutation<PatchUserProfileSettingsResponse, UserProfileSettingsPatchParams>({
      query: (body) => ({
        url: `/user/profile-settings`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Profile'],
      onQueryStarted: onQueryStartedErrorToast,
    }),
    getNearbyShips: builder.mutation<
      WithinDistanceTargetCatalogResponse,
      { requestParam: number; body: WithinDistanceTargetCatalogRequest }
    >({
      query: ({ body, requestParam }) => {
        return {
          url: `/targets/${requestParam}/within-distance`,
          method: 'POST',
          body,
        };
      },
    }),
    getUserNotifications: builder.query<GetUserNotificationsResponse, { datetime?: string }>({
      query: ({ datetime }) => ({
        url: `/notifications?from=&datetime=${datetime}`,
      }),
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName;
      },
      merge: (currentCache, newItems, { arg }) => {
        if (currentCache.notifications && arg.datetime && arg.datetime !== '..') {
          if (newItems?.notifications) {
            currentCache.notifications?.push(...newItems.notifications);
          }
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg;
      },
    }),
    getUserApikey: builder.query<GetUserApiKeyResponse, void>({
      query: () => ({
        url: `/user/profile/apikey`,
      }),
      providesTags: [{ type: 'Profile' }],
      onQueryStarted: onQueryStartedErrorToast,
    }),
    getNtfRules: builder.query<GetNotificationRulesResponse, void>({
      query: () => ({
        url: '/notification-rules',
      }),

      providesTags: [{ type: 'Notifications-rules' }],
      onQueryStarted: onQueryStartedErrorToast,
    }),
    deleteNtfRule: builder.mutation<DeleteUserNotificationRuleResponse, string>({
      query: (key) => ({
        url: `/notification-rules/${key}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Notifications-rules'],
    }),
    getShipNote: builder.query<GetUserNotesForObjectDefinitionResponse, number | undefined>({
      query: (mmsi) => ({
        url: `/notes/object-definition/MMSI:${mmsi}`,
      }),
      providesTags: [{ type: 'Ship-note' }],

      onQueryStarted: onQueryStartedErrorToast,
    }),
    createShipNote: builder.mutation<CreateUserNoteResponse, { mmsi?: number; message: string }>({
      query: ({ mmsi, message }) => ({
        url: `/notes/object-definition/MMSI:${mmsi}`,
        method: 'POST',
        body: {
          creationParams: {
            message,
          },
        },
      }),
      invalidatesTags: ['Ship-note'],
    }),
    editShipNote: builder.mutation<RecreateUserNoteResponse, { key?: string; message: string }>({
      query: ({ key, message }) => ({
        url: `/notes/${key}`,
        method: 'POST',
        body: {
          recreationParams: {
            message,
          },
        },
      }),
      invalidatesTags: ['Ship-note'],
    }),
    deleteNote: builder.mutation<DeleteUserNoteResponse, string | undefined>({
      query: (key) => ({
        url: `/notes/${key}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Ship-note'],
    }),
    getSystemConfiguration: builder.query<GetUserSystemConfigurationResponse, void>({
      query: () => ({
        url: `/user/system-configuration`,
        withCredentials: true,
        invalidatesTags: ['User'],
      }),
      onQueryStarted: onQueryStartedErrorToast,
    }),
    createNotificationRule: builder.mutation<CreateUserNotificationRuleResponse, any>({
      query: (body) => ({
        url: '/notification-rules',
        withCredetials: true,
        method: 'POST',
        body: { ruleCreationParams: body },
      }),
      invalidatesTags: ['Notifications-rules'],
    }),
    getNotesList: builder.mutation<SearchUserNotesForObjectDefinitionResponse, any[] | undefined>({
      query: (objDefinitions) => {
        return {
          url: `/notes/search/object-definition`,
          body: { searchParams: { objectDefinitions: objDefinitions } },
          method: 'POST',
          invalidatesTags: ['Ship-note'],
        };
      },
      onQueryStarted: onQueryStartedErrorToast,
    }),
    getAnalyticsMap: builder.mutation<any, any>({
      query: (body) => ({
        url: '/analytics/wms/pos',
        method: 'POST',
        contentType: 'image/png',
        cache: 'no-cache',
        overrideExisting: false,
        withCredentials: true,
        body,
        responseHandler: (response: { blob: () => any }) => response.blob(),
      }),
      onQueryStarted: onQueryStartedErrorToast,
    }),
    getPlayback: builder.mutation<MapPlaybackResponse, PlaybackSearchParams>({
      query: (body) => ({
        url: '/playback',
        method: 'POST',
        withCredentials: true,
        body: {
          value: body,
        },
      }),
      onQueryStarted: onQueryStartedErrorToast,
    }),
    getAnchorage: builder.mutation<GetAnchoragesOnMapResponse, any>({
      query: (body) => ({
        url: '/anchorage/map',
        method: 'POST',
        withCredentials: true,
        body: {
          value: body,
        },
      }),
      onQueryStarted: onQueryStartedErrorToast,
    }),
    getAnchorageEventsData: builder.mutation<TargetAnchorageEventsResponse, { body: TargetAnchorageEventsRequest; mmsi: string }>(
      {
        query: ({ mmsi, body }) => {
          return {
            url: `/targets/${mmsi}/anchorage-events`,
            headers: { accept: 'application/json', 'Content-type': 'application/json' },
            method: 'POST',
            body,
            cache: 'no-cache',
            withCredentials: true,
            overrideExisting: false,
          };
        },
        onQueryStarted: onQueryStartedErrorToast,
      },
    ),
    getVesselsGeoInfo: builder.mutation<PointGeoInfoResponse, { body: PointGeoInfoRequest; type: string[] }>({
      query: ({ body, type }) => ({
        url: '/point-geo-info',
        method: 'POST',
        withCredentials: true,
        params: {
          type,
        },
        body,
      }),
      onQueryStarted: onQueryStartedErrorToast,
    }),
    getSystemNotifications: builder.query<GetUserSystemNotificationResponse, void>({
      query: () => {
        return {
          url: `/system-notifications`,
        };
      },
      onQueryStarted: onQueryStartedErrorToast,
    }),
    getSafetyEvents: builder.query<LastSafetyEventsResponse, { datetime?: string }>({
      query: ({ datetime }) => {
        return {
          url: `/last-safety-events?datetime=${datetime}`,
        };
      },
      onQueryStarted: onQueryStartedErrorToast,
    }),
    getTGSubscription: builder.query<GetMessageNotificationsTelegramSubscriptionResponse, void>({
      query: () => {
        return {
          url: '/message-notifications/telegram/subscription',
        };
      },
      onQueryStarted: onQueryStartedErrorToast,
    }),
    getTGSubscriptionToken: builder.query<GetMessageNotificationsTelegramSubscriptionTokenResponse, void>({
      query: () => {
        return {
          url: '/message-notifications/telegram/subscription/token',
        };
      },
      onQueryStarted: onQueryStartedErrorToast,
    }),
    deleteTGSubscription: builder.mutation<DeleteMessageNotificationsTelegramSubscriptionResponse, void>({
      query: () => ({
        url: `/message-notifications/telegram/subscription`,
        method: 'DELETE',
        cache: 'no-cache',
        withCredentials: true,
      }),
    }),
    getWorldFleet: builder.query<WorldFleetResponse, { datetime: string }>({
      query: ({ datetime }) => {
        return {
          url: `/aggregate/world-fleet?datetime=${datetime}`,
        };
      },
      onQueryStarted: onQueryStartedErrorToast,
    }),
  }),
});

export const {
  useCreateNotificationRuleMutation,
  useCreateShipNoteMutation,
  useDeleteNoteMutation,
  useDeleteNtfRuleMutation,
  useDeleteTGSubscriptionMutation,
  useEditShipNoteMutation,
  useGetAnalyticsMapMutation,
  useGetAnchorageEventsDataMutation,
  useGetAnchorageMutation,
  useGetArrivalsCsvMutation,
  useGetFilterOptionsQuery,
  useGetMapSnapshotMutation,
  useGetNearbyShipsMutation,
  useGetNotesListMutation,
  useGetNtfRulesQuery,
  useGetPlaybackMutation,
  useGetPortArrivalsDataMutation,
  useGetPortArrivalsFiltersOptionsQuery,
  useGetPortEventsDataMutation,
  useGetPortTargetCatalogFilterOptionsQuery,
  useGetPortTargetCatalogMutation,
  useGetShipNoteQuery,
  useGetSimpleRouteMutation,
  useGetTargetsMutation,
  useGetTerminalCardQuery,
  useGetTerminalCatalogMutation,
  useGetTerminalsOnMapMutation,
  useGetTerminalTargetCatalogMutation,
  useGetUserApikeyQuery,
  useGetVesselCatalogInAreaMutation,
  useGetVesselCatalogMutation,
  useGetVesselEventsDataMutation,
  useGetVesselsGeoInfoMutation,
  useGetVesselsInPortCsvMutation,
  useLazyGetAreaCardQuery,
  useLazyGetAreasCatalogQuery,
  useLazyGetHistoryQuery,
  useLazyGetPortCardQuery,
  useLazyGetSafetyEventsQuery,
  useLazyGetSystemConfigurationQuery,
  useLazyGetSystemNotificationsQuery,
  useLazyGetTGSubscriptionQuery,
  useGetTGSubscriptionQuery,
  useLazyGetTGSubscriptionTokenQuery,
  useLazyGetUserFleetQuery,
  useLazyGetUserNotificationsQuery,
  useLazyGetUserProfileQuery,
  useLazyGetWorldFleetQuery,
  useUpdateUserProfileSettingsMutation,
} = facadeApi;
